import type { TFunction } from "i18next"
import { useMemo } from "react"
import useTranslate, { type UseTranslateReturn } from "./useTranslate"
import { getTranslateMethods } from "./utils"

function scopeLookupKey(
  key: string | string[],
  prefix?: string
): string | string[] {
  if (!prefix) return key
  if (Array.isArray(key)) return key.map((k) => `${prefix}.${k}`)
  return `${prefix}.${key}`
}

/** Applies `prefix.` to string / string[] keys; leaves tagged-template usage unchanged. */
function createPrefixedTranslator(
  translate: TFunction,
  prefix?: string
): TFunction {
  return ((...args: Parameters<TFunction>) => {
    if (!prefix) return translate(...args)
    const [first, ...rest] = args
    if (typeof first === "string") {
      return translate(`${prefix}.${first}`, ...rest)
    }
    if (Array.isArray(first)) {
      const withRaw = first as readonly string[] & { raw?: readonly string[] }
      if ("raw" in withRaw && Array.isArray(withRaw.raw)) {
        return translate(...args)
      }
      return translate(
        (first as string[]).map((k) => `${prefix}.${k}`) as never,
        ...rest
      )
    }
    return translate(...args)
  }) as TFunction
}

/**
 * Prefix-scoped translation helpers for feature areas (e.g. auth, cover_page).
 * Must run under the same react-i18next tree as the main app (common namespace).
 */
export const useScopedTranslation = (prefix?: string): UseTranslateReturn => {
  const { t, tc, tca, conditionalT, i18n, Trans, ready } = useTranslate()

  return useMemo((): UseTranslateReturn => {
    if (!prefix) {
      return { t, tc, tca, conditionalT, i18n, Trans, ready }
    }

    const prefixedTranslator = createPrefixedTranslator(
      t as unknown as TFunction,
      prefix
    )
    const baseMethods = getTranslateMethods(prefixedTranslator, i18n)
    return {
      ...baseMethods,
      conditionalT: (key, options, customTranslator) => {
        const scoped = scopeLookupKey(key, prefix)
        return i18n.exists(scoped as never)
          ? String(
              (customTranslator || prefixedTranslator)(scoped as never, options)
            )
          : ""
      },
      i18n,
      Trans,
      ready
    }
  }, [prefix, t, tc, tca, conditionalT, i18n, Trans, ready])
}

export default useScopedTranslation
